// Paket düzenleyici (P2 yerleşimi) saf yardımcıları: taslağı yansıtan sayaçlar, süzgeç + paket içi
// arama, "Neden pakette?" kararı ve kategori satırı toplu çıkarma. Ağaç ve başlık aynı kaynaktan
// okur — tek sayaç tanımı. Yapı önce okunur (R5-1): kırılım grup/üye satırı kaynağı ne olursa olsun
// "Kırılım — <toplam> toplamı için" der; sonra otomatik bağımlılık, sonra AI kaynağı, sonra elle.

import type { KpiPackageItem } from "@mahir/shared";
import type { TriState } from "./kpi-set-editor";
import {
  draftMemberIds,
  missingMemberIds,
  requiredByOf,
  toggleDraftItem,
  type DraftContext,
  type PackageDraft,
} from "./kpi-package-draft";

export type PackageFilter = "all" | "auto" | "warning" | "changed";

export interface EditorCounts {
  total: number;
  auto: number;
  warning: number;
  changed: number;
}

export type WhyInPackage =
  | { kind: "manual" }
  | { kind: "auto"; forLabel: string }
  | { kind: "breakdown"; totalLabel: string }
  | { kind: "ai"; reason: string | null };

const isGroupNode = (ctx: DraftContext, id: string): boolean =>
  ctx.byId.get(id)?.nodeKind === "breakdown_group";
const isMemberNode = (ctx: DraftContext, id: string): boolean => {
  const m = ctx.byId.get(id);
  return m?.dimensionMember !== null && m?.dimensionMember !== undefined;
};

/** Eksik kırılım üyesi olan (kısmi) toplamlar. */
export function partialTotalIds(members: ReadonlySet<string>, ctx: DraftContext): Set<string> {
  const out = new Set<string>();
  for (const mem of missingMemberIds(members, ctx)) {
    const grp = ctx.byId.get(mem)?.parentId;
    const total = grp ? ctx.byId.get(grp)?.parentId : null;
    if (total) out.add(total);
  }
  return out;
}

/** Ağaçta görünen kalem kimlikleri: sunucu kalemleri ∪ taslak üyeleri. */
export function shownItemIds(base: readonly KpiPackageItem[], draft: PackageDraft): string[] {
  return [...new Set([...base.map((b) => b.metricId), ...draftMemberIds(base, draft)])];
}

/** Taslakta değişen kalemler: eklenen, çıkarılan (tabandan) ve varsayılanı değişen üyeler. */
export function changedItemIds(base: readonly KpiPackageItem[], draft: PackageDraft): Set<string> {
  const baseById = new Map(base.map((b) => [b.metricId, b]));
  const out = new Set<string>();
  for (const id of draft.added) if (!baseById.has(id)) out.add(id);
  for (const id of draft.removed) if (baseById.has(id)) out.add(id);
  for (const [id, d] of draft.defaults) {
    const item = baseById.get(id);
    if (!item || draft.removed.has(id)) continue;
    if (
      d.unitDefault !== (item.unitDefault ?? null) ||
      d.activeFormulaId !== (item.activeFormulaId ?? null) ||
      d.showChange !== (item.showChange ?? null)
    ) {
      out.add(id);
    }
  }
  return out;
}

function warningIds(members: ReadonlySet<string>, ctx: DraftContext): Set<string> {
  const out = new Set<string>();
  for (const id of members) if (ctx.byId.get(id)?.status === "deprecated") out.add(id);
  for (const id of partialTotalIds(members, ctx)) out.add(id);
  return out;
}

/** Başlık sayaçları — taslağı yansıtır (15 üye + 3 ekleme − 1 çıkarma → 17). */
export function editorCounts(
  base: readonly KpiPackageItem[],
  draft: PackageDraft,
  ctx: DraftContext,
): EditorCounts {
  const members = new Set(draftMemberIds(base, draft));
  const autoIds = new Set(base.filter((b) => b.autoAdded).map((b) => b.metricId));
  return {
    total: members.size,
    auto: [...members].filter((id) => autoIds.has(id)).length,
    warning: warningIds(members, ctx).size,
    changed: changedItemIds(base, draft).size,
  };
}

const norm = (s: string): string => s.toLocaleLowerCase("tr-TR").trim();

/** Süzgeç + paket içi arama (ad ya da kod) sonrası kalem kimlikleri. */
export function filterMemberIds(
  ids: readonly string[],
  filter: PackageFilter,
  search: string,
  base: readonly KpiPackageItem[],
  draft: PackageDraft,
  ctx: DraftContext,
): Set<string> {
  const members = new Set(draftMemberIds(base, draft));
  const autoIds = new Set(base.filter((b) => b.autoAdded).map((b) => b.metricId));
  const warn = warningIds(members, ctx);
  const changed = changedItemIds(base, draft);
  const q = norm(search);
  const out = new Set<string>();
  for (const id of ids) {
    if (filter === "auto" && !(autoIds.has(id) && members.has(id))) continue;
    if (filter === "warning" && !warn.has(id)) continue;
    if (filter === "changed" && !changed.has(id)) continue;
    if (q) {
      const m = ctx.byId.get(id);
      if (!m || !(norm(m.label).includes(q) || norm(m.code).includes(q))) continue;
    }
    out.add(id);
  }
  return out;
}

/** "Neden pakette?" — yapı önce (R5-1), sonra otomatik bağımlılık, AI kaynağı, elle. */
export function whyInPackage(
  id: string,
  base: readonly KpiPackageItem[],
  draft: PackageDraft,
  ctx: DraftContext,
): WhyInPackage {
  if (isGroupNode(ctx, id) || isMemberNode(ctx, id)) {
    const parent = ctx.byId.get(id)?.parentId ?? null;
    const total = isGroupNode(ctx, id)
      ? parent
      : parent
        ? (ctx.byId.get(parent)?.parentId ?? null)
        : null;
    return {
      kind: "breakdown",
      totalLabel: (total ? ctx.byId.get(total)?.label : undefined) ?? "",
    };
  }
  const item = base.find((b) => b.metricId === id);
  if (item?.autoAdded) {
    const members = new Set(draftMemberIds(base, draft));
    return { kind: "auto", forLabel: (requiredByOf(members, ctx).get(id) ?? []).join(", ") };
  }
  const src = item ? { source: item.source, reason: item.sourceReason } : draft.sources.get(id);
  if (src?.source === "ai") return { kind: "ai", reason: src.reason ?? null };
  return { kind: "manual" };
}

/** Kategori satırı: alt ağaçtaki kalemler; `visible` (süzgeç/arama sonucu) verilirse yalnız görünenler. */
export function categoryItemIds(
  catId: string,
  shown: ReadonlySet<string>,
  ctx: DraftContext,
  visible: ReadonlySet<string> | null = null,
): string[] {
  const out: string[] = [];
  const walk = (id: string): void => {
    for (const k of ctx.kids.get(id) ?? []) {
      if (shown.has(k.id) && (!visible || visible.has(k.id))) out.push(k.id);
      walk(k.id);
    }
  };
  walk(catId);
  return out;
}

/** Üç durumlu kutu: tümü üye → all; hiçbiri → none; karışık → some. */
export function categoryBulkState(
  catId: string,
  shown: ReadonlySet<string>,
  members: ReadonlySet<string>,
  ctx: DraftContext,
  visible: ReadonlySet<string> | null = null,
): TriState {
  const ids = categoryItemIds(catId, shown, ctx, visible);
  const on = ids.filter((id) => members.has(id)).length;
  return ids.length === 0 || on === 0 ? "none" : on === ids.length ? "all" : "some";
}

/** Tümü üyeyse hepsini taslaktan çıkarır; değilse çıkarılmış olanları geri alır. */
export function bulkToggleCategory(
  base: readonly KpiPackageItem[],
  draft: PackageDraft,
  catId: string,
  shown: ReadonlySet<string>,
  ctx: DraftContext,
  visible: ReadonlySet<string> | null = null,
): PackageDraft {
  const members = new Set(draftMemberIds(base, draft));
  const ids = categoryItemIds(catId, shown, ctx, visible);
  const allOn = ids.length > 0 && ids.every((id) => members.has(id));
  let out = draft;
  for (const id of ids) {
    const on = draftMemberIds(base, out).includes(id);
    if (allOn && on) out = toggleDraftItem(base, out, id, false, ctx);
    else if (!allOn && !on) out = toggleDraftItem(base, out, id, true, ctx);
  }
  return out;
}

/**
 * Kayıt reddi (422 `details.codes`: müşteriye özel / kategori / kırılım üyesi gibi sorunlu göstergeler):
 * ağaçta vurgulanacak kimlikler + hata metnine eklenecek ad listesi (kod katalogda yoksa kodun kendisi).
 */
export function rejectedItems(
  details: unknown,
  ctx: Pick<DraftContext, "byCode">,
): { ids: Set<string>; names: string[] } {
  const codes = (details as { codes?: unknown } | null | undefined)?.codes;
  const ids = new Set<string>();
  const names: string[] = [];
  if (!Array.isArray(codes)) return { ids, names };
  for (const c of codes) {
    const m = ctx.byCode.get(String(c));
    if (m) ids.add(m.id);
    names.push(m?.label ?? String(c));
  }
  return { ids, names };
}
